import { Box, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { statusLogsSelector, useGetTransactionDetailsQuery } from '@/api/v1';
import SettlementTimeIcon from '@/assets/icons/settlement-time-icon.svg?react';
import { Alert } from '@/components/molecules/Alert';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { TransactionDetailsDrawerStatusItem } from '@/components/molecules/TransactionDetailsDrawerStatusItem';
import { TransactionDetailsDrawerStatusPlaceholderItem } from '@/components/molecules/TransactionDetailsDrawerStatusPlaceholderItem';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { TransactionStatusLog } from '@/types/transactionStatusLog';
import { StatusPlaceholder } from '@/types/transactionStatusLogs';

interface StatusDisplayInfo {
  readonly label: string;
  readonly status: 'completed' | 'in-progress' | 'pending';
  readonly description: string;
  readonly date: string;
}

interface Props {
  readonly statusLogs?: readonly TransactionStatusLog[];
  readonly nextStatus?: readonly string[];
}

export const TransactionDetailsDrawerStatus: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { transactionId = '' } = useParams<{ readonly transactionId: string | undefined }>();
  const { isLoading, error } = useSelector(statusLogsSelector(transactionId));
  const { data: transaction } = useGetTransactionDetailsQuery(transactionId);
  const expectedSettlementTimeDetails =
    transaction?.transactionDetails.expectedSettlementTimeDetails;
  const { statusLogs = [], nextStatus = [] } = props;
  const tr = useTranslator();

  // Reverse the array to show chronological order (oldest first)
  const sortedStatusLogs = React.useMemo(
    (): readonly TransactionStatusLog[] =>
      [...statusLogs]
        .filter((statusLog: TransactionStatusLog): boolean => !statusLog.internal)
        .reverse(),
    [statusLogs],
  );

  // Create next status items if they exist
  const nextStatusItems = React.useMemo(
    (): readonly StatusPlaceholder[] =>
      nextStatus.map(
        (status: string): StatusPlaceholder => ({
          label: tr(status),
          status: 'pending' as const,
          description: tr(`${status}_DESC`),
          date: null,
        }),
      ),
    [nextStatus, tr],
  );

  const dateFormatter = useDateFormatter('MMM d, yyyy | hh:mm a');
  const mapStatusToDisplayInfo = React.useCallback(
    (
      statusLog: TransactionStatusLog,
      index: number,
      transactionStatusLogs: readonly TransactionStatusLog[],
    ): StatusDisplayInfo => {
      const { status } = statusLog;

      const mappedInfo = {
        label: status,
        description: `${status}_DESC`,
        date: dateFormatter.parseAndFormat(statusLog.createdAt),
      };

      if (index === transactionStatusLogs.length - 1 && !statusLog.final) {
        return {
          ...mappedInfo,
          status: 'in-progress',
        };
      }

      return {
        ...mappedInfo,
        status: 'completed',
      };
    },
    [dateFormatter],
  );

  const renderLogItem = React.useCallback(
    (statusLog: TransactionStatusLog, index: number): React.ReactElement => {
      const displayInfo = mapStatusToDisplayInfo(statusLog, index, sortedStatusLogs);

      return (
        <Box key={`box-${statusLog.status}-${index}`} position="relative">
          {(nextStatusItems.length > 0 || index + 1 < sortedStatusLogs.length) && (
            <Box sx={styles.divider} />
          )}
          <TransactionDetailsDrawerStatusItem
            key={`${statusLog.status}-${index}`}
            label={displayInfo.label}
            status={displayInfo.status}
            date={displayInfo.date}
            description={displayInfo.description}
          />
        </Box>
      );
    },
    [mapStatusToDisplayInfo, nextStatusItems.length, sortedStatusLogs],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body2" color="textSecondary">
          {tr('LOADING_TRANSACTION_STATUS')}
        </Typography>
      </Box>
    );
  }

  if (error || !statusLogs || statusLogs.length === 0) {
    return <></>;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} pb={2}>
      <Box display="flex" flexDirection="column" position="relative" py={2} gap={4}>
        {sortedStatusLogs.map(renderLogItem)}
        {nextStatusItems.map(
          (nextStatusItem: StatusPlaceholder, index: number): React.ReactElement => (
            <Box key={`box-${nextStatusItem.status}-${index}`} position="relative">
              {index + 1 < nextStatusItems.length && <Box sx={styles.divider} />}
              <TransactionDetailsDrawerStatusPlaceholderItem
                key={`next-${nextStatusItem.label}-${index}`}
                label={tr(nextStatusItem.label)}
                status={nextStatusItem.status}
              />
            </Box>
          ),
        )}
      </Box>
      {expectedSettlementTimeDetails && (
        <Alert
          sx={styles.alert}
          variant="info"
          leftIcon={<SettlementTimeIcon stroke="currentColor" />}
          // removing the icon until the product team makes up the copy for the info button
          // rightIcon={<InfoIcon fill={theme.palette.custom.LightGray} />}
        >
          <ExpectedSettlementTimeInfo
            expectedSettlementTimeDetails={expectedSettlementTimeDetails}
            settlementTimeTextStyles={styles.settlementTimeText}
          />
        </Alert>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  divider: {
    width: '1px',
    backgroundColor: 'custom.Inactive',
    position: 'absolute',
    top: '27px',
    bottom: '-28px',
    left: '7px',
  },
  alert: {
    py: 2,
  },
  settlementTimeText: {
    color: 'primary.main',
  },
};
